<script setup lang="ts">
import { type ComponentPublicInstance, computed, nextTick, ref, watch } from 'vue';
import type { IncorporationLine, RequirementSummary } from '@/api/erp-integration';
import { REQUIREMENT_FREQUENCIES } from '@/api/planning/requirements';
import { useCareerSkills } from '@/composables/useCareerSkills';
import { ALL_CAREERS, careerLabel } from '@/utils/career-metadata';
import { FREQUENCY_LABELS_ES, PERIOD_NOUNS_ES } from '@/utils/monthly-expectation';
import {
  MAX_DURATION_MIN, MAX_VISITS_PER_PERIOD, type RowDraft, type RowMode, requirementTitle, rowErrors,
} from './erp-line-draft';
import { durationText, visitsText } from './erp-format';

// One Requirement a Service line becomes or feeds, always open: a new row
// starts from what the AI read in the notes (already in its fields), and the
// coordinator confirms or corrects it. The notes themselves are on the left of
// the dialog, so the row never repeats them.

const props = defineProps<{
  line: IncorporationLine;
  label: string;
  requirements: RequirementSummary[];
  removable: boolean;
  showErrors: boolean;
}>();
const emit = defineEmits<{ remove: [] }>();
const row = defineModel<RowDraft>({ required: true });

const MINUTES_PER_HOUR = 60;
// Careers whose visits are shifts of hours; the others' last minutes.
const SHIFT_CAREERS = ['caregiver', 'nurse_technician', 'auxiliary_nurse'];

const root = ref<ComponentPublicInstance | null>(null);
const own = computed(() => row.value.ownRequirementId !== null);
// A placed line whose product is no longer mapped can only stay as it is.
const frozen = computed(() => props.line.kind === 'unmapped');
// Requirements the row may continue or move to (never the one it is).
const others = computed(() => props.requirements.filter((requirement) => (
  requirement.id !== row.value.ownRequirementId
)));
const modes = computed(() => {
  const continues = others.value.length ? [{ value: 'continue', title: own.value ?
    'Moverla a otro requerimiento' : 'Continuar un requerimiento del paciente' }] : [];
  if (own.value) {
    return [{ value: 'keep', title: 'Dejar el requerimiento como está' },
      { value: 'update', title: 'Actualizar el requerimiento' }, ...continues];
  }

  return [{ value: 'create', title: 'Crear requerimiento' }, ...continues];
});
const careers = ALL_CAREERS.map((career) => ({ value: career, title: careerLabel(career) }));
const frequencies = REQUIREMENT_FREQUENCIES.map((value) => ({
  value, title: FREQUENCY_LABELS_ES[value],
}));
const requirementItems = computed(() => others.value.map((requirement) => ({
  value: requirement.id, title: requirementTitle(requirement, careerLabel(requirement.career)),
})));
const withValues = computed(() => ['create', 'update'].includes(row.value.mode));

const errors = computed(() => (props.showErrors ? rowErrors(row.value) : {}));

// Back to "keep", a row shows its Requirement's values as they are.
function update(changes: Partial<RowDraft>) {
  const restored = changes.mode === 'keep' && row.value.original ? row.value.original : {};
  row.value = { ...row.value, ...changes, ...restored };
}

const career = computed(() => row.value.career);
// A row keeps the skill its Requirement has; changing the career of an edited
// row clears it (a skill belongs to one career).
const skillId = computed({
  get: () => row.value.skillId,
  set: (value) => {
    if (withValues.value) update({ skillId: value as number | null });
  },
});
const { careerSkills } = useCareerSkills(career, skillId, null, computed(() => !!row.value.skillId));
const skillName = computed(() => (
  careerSkills.value?.find((skill) => skill.id === row.value.skillId)?.name ?? null
));

function continueSummary(): string {
  const target = props.requirements.find((item) => item.id === row.value.requirementId);

  return target ? `Continúa: ${requirementTitle(target, careerLabel(target.career))}` :
    'Continúa un requerimiento del paciente';
}

// A row whose values are not edited here says what it is in one line.
const summary = computed(() => {
  if (withValues.value) return null;
  if (row.value.mode === 'continue') return continueSummary();
  const name = row.value.career ? careerLabel(row.value.career) : 'Sin especialidad';
  const parts = [skillName.value ? `${name} (${skillName.value})` : name,
    visitsText(row.value.visitsPerPeriod, row.value.frequency) ?? 'visitas por definir',
    durationText(row.value.durationMin)];

  return parts.filter(Boolean).join(' · ');
});

function toNumber(value: string | number | null): number | null {
  if (value === null || value === '') return null;

  return Number(value);
}

// The duration is entered in hours (shift careers) or minutes (the others),
// which the clock switches, and always kept in minutes.
type DurationUnit = 'hours' | 'minutes';
const chosenUnit = ref<DurationUnit | null>(null);
const durationUnit = computed<DurationUnit>(() => chosenUnit.value ??
  (SHIFT_CAREERS.includes(row.value.career ?? '') ? 'hours' : 'minutes'));
watch(() => row.value.career, () => {
  chosenUnit.value = null;
});

const switchLabel = computed(() => (
  durationUnit.value === 'hours' ? 'Cambiar a minutos' : 'Cambiar a horas'
));

function switchUnit() {
  chosenUnit.value = durationUnit.value === 'hours' ? 'minutes' : 'hours';
}
const durationValue = computed(() => {
  const minutes = row.value.durationMin;
  if (minutes === null) return null;

  return durationUnit.value === 'hours' ? minutes / MINUTES_PER_HOUR : minutes;
});

function setDuration(value: string | number | null) {
  const amount = toNumber(value);
  if (amount === null) {
    update({ durationMin: null });
    return;
  }

  update({ durationMin: durationUnit.value === 'hours' ?
    Math.round(amount * MINUTES_PER_HOUR) : amount });
}

// Moves the focus to the row's first field (a row just added).
async function focusFirst() {
  await nextTick();
  const element = root.value?.$el as HTMLElement | undefined;
  element?.querySelector<HTMLElement>('input:not([type="hidden"]), [role="combobox"]')?.focus();
}

defineExpose({ focusFirst });
</script>
<template>
  <v-card
    ref="root"
    class="pa-3"
  >
    <div
      v-if="summary || row.mode === 'keep' || removable"
      class="d-flex flex-wrap align-center ga-2 mb-2"
    >
      <span
        v-if="summary"
        class="text-body-2 font-weight-medium"
      >{{ summary }}</span>
      <v-chip
        v-if="row.mode === 'keep'"
        size="x-small"
        label
      >
        Actual
      </v-chip>
      <v-spacer />
      <v-btn
        v-if="removable"
        size="small"
        variant="text"
        color="error"
        :aria-label="`Quitar ${label}`"
        @click="emit('remove')"
      >
        Quitar
      </v-btn>
    </div>
    <v-row
      v-if="!frozen"
      dense
    >
      <v-col
        v-if="modes.length > 1"
        cols="12"
      >
        <v-select
          :model-value="row.mode"
          :items="modes"
          label="Qué hacer"
          variant="outlined"
          density="compact"
          hide-details="auto"
          @update:model-value="(mode: RowMode) => update({ mode })"
        />
      </v-col>
      <v-col
        v-if="row.mode === 'continue'"
        cols="12"
      >
        <v-select
          :model-value="row.requirementId"
          :items="requirementItems"
          label="Requerimiento del paciente"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :error-messages="errors.requirement"
          @update:model-value="(requirementId: number) => update({ requirementId })"
        />
      </v-col>
      <template v-if="withValues">
        <v-col
          cols="12"
          sm="6"
        >
          <v-select
            :model-value="row.career"
            :items="careers"
            label="Especialidad"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :error-messages="errors.career"
            @update:model-value="(value: string) => update({ career: value })"
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-select
            :model-value="row.frequency"
            :items="frequencies"
            label="Frecuencia"
            variant="outlined"
            density="compact"
            hide-details="auto"
            @update:model-value="(frequency: RowDraft['frequency']) => update({ frequency })"
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-text-field
            :model-value="row.visitsPerPeriod"
            type="number"
            :min="1"
            :max="MAX_VISITS_PER_PERIOD"
            :label="`Visitas por ${PERIOD_NOUNS_ES[row.frequency]}`"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :error-messages="errors.visits"
            @update:model-value="(value) => update({ visitsPerPeriod: toNumber(value) })"
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-text-field
            :model-value="durationValue"
            type="number"
            :min="1"
            :max="durationUnit === 'hours' ? MAX_DURATION_MIN / MINUTES_PER_HOUR : MAX_DURATION_MIN"
            :label="`Duración por visita (${durationUnit === 'hours' ? 'horas' : 'minutos'})`"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :error-messages="errors.duration"
            @update:model-value="setDuration"
          >
            <template #append-inner>
              <v-tooltip
                location="top"
                :text="switchLabel"
              >
                <template #activator="{ props: tooltipProps }">
                  <v-btn
                    v-bind="tooltipProps"
                    icon="mdi-clock-outline"
                    variant="text"
                    size="small"
                    density="comfortable"
                    :aria-label="`${switchLabel} en ${label}`"
                    @click="switchUnit"
                  />
                </template>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
      </template>
    </v-row>
  </v-card>
</template>
